import { useId } from 'react'
import { useForm, Controller, type Resolver } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import type { AccountSummary } from '@/api/accounts'
import type { TransactionSummary } from '@/api/transactions'
import { AmountCalculatorInput } from '@/components/AmountCalculatorInput'
import { DatePicker } from '@/components/DatePicker'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { formatDate } from '@/lib/formatDate'
import { today } from '@/lib/transactionDateRangeFilter'
import { kindInfo } from './addKinds'
import { AmountHero, Field, FormShell, InfoNote } from './fields'
import {
  errorText,
  transactableAccounts,
  transferSchema,
  useCreateTransaction,
  type TransferForm as TransferValues,
} from './formLogic'

export function TransferForm({
  accounts,
  prefill,
  initialFromAccountId,
  initialToAccountId,
  onBack,
  onClose,
}: {
  accounts: AccountSummary[]
  initialFromAccountId?: string
  initialToAccountId?: string
  /** Duplicate: start from this transfer leg's accounts and amount, dated today. */
  prefill?: TransactionSummary
  onBack?: () => void
  onClose: () => void
}) {
  const formId = useId()
  const info = kindInfo('transfer')
  const usable = transactableAccounts(accounts)
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<TransferValues>({
    resolver: zodResolver(transferSchema) as Resolver<TransferValues>,
    defaultValues: prefill
      ? {
          date: today(),
          amount: prefill.amount,
          description: prefill.description,
          // The row may be either leg; the money still went from OUT's account to IN's.
          fromAccountId:
            (prefill.transferDirection === 'IN'
              ? prefill.partnerAccountId
              : prefill.financialAccountId) ?? '',
          toAccountId:
            (prefill.transferDirection === 'IN'
              ? prefill.financialAccountId
              : prefill.partnerAccountId) ?? '',
        }
      : {
          date: today(),
          description: 'Transfer',
          fromAccountId: initialFromAccountId ?? '',
          toAccountId: initialToAccountId ?? '',
        },
  })
  const create = useCreateTransaction(onClose)

  const accountSelect = (name: 'fromAccountId' | 'toAccountId', label: string) => (
    <Field label={label} error={errors[name]?.message}>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Select
            aria-label={label}
            value={field.value ?? ''}
            onChange={field.onChange}
            onBlur={field.onBlur}
          >
            <option value="">Select account</option>
            {usable.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Select>
        )}
      />
    </Field>
  )

  return (
    <FormShell
      title={info.label}
      Icon={info.Icon}
      tone={info.tone}
      onBack={onBack}
      onClose={onClose}
      formId={formId}
      submitLabel="Save Transfer"
      submitting={create.isPending}
      error={create.error ? errorText(create.error) : null}
    >
      <form
        id={formId}
        onSubmit={handleSubmit((v) => create.mutate({ type: 'TRANSFER', ...v }))}
        className="flex flex-col gap-4"
      >
        {prefill && (
          <InfoNote>
            Copy of “{prefill.description}” from {formatDate(prefill.date)}. The date is set to
            today.
          </InfoNote>
        )}
        <AmountHero error={errors.amount?.message}>
          <Controller
            control={control}
            name="amount"
            render={({ field }) => (
              <AmountCalculatorInput
                value={field.value?.toString() ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                placeholder="0.00"
              />
            )}
          />
        </AmountHero>
        {accountSelect('fromAccountId', 'From')}
        {accountSelect('toAccountId', 'To')}
        <Field label="Description" error={errors.description?.message}>
          <Input aria-label="Description" {...register('description')} />
        </Field>
        <Field label="Date" error={errors.date?.message}>
          <Controller
            control={control}
            name="date"
            render={({ field }) => <DatePicker value={field.value} onChange={field.onChange} />}
          />
        </Field>
        <InfoNote>
          Moves money between your own accounts. Not counted as spending or income.
        </InfoNote>
      </form>
    </FormShell>
  )
}
